<template>
  <c-modal
    v-model:open="computedOpen"
    :class="b()"
    :content-class="b(formModifiers)"
    size="500"
    @close="reset"
  >
    <div
      v-if="eifuStore.runningRequests.apiReportProblem"
      :class="b('progress')"
    >
      <e-progress />
    </div>
    <template v-else-if="showConfirmation">
      <h2 :class="b('title')">
        {{ matchThemeTranslation(themeConfig.translations.titleConfirmation) }}
      </h2>
      <p>
        {{ matchThemeTranslation(themeConfig.translations.textConfirmationMessage) }}
      </p>
      <div :class="b('close-button-wrapper')">
        <e-button
          :class="b('close-button')"
          :width="$viewport.isSm ? 'auto' : 'full'"
          variant="primary"
          spacing="600"
          @click="close"
        >
          {{ $t('c-electronic-instruction-problem.buttonLabelClose') }}
        </e-button>
      </div>
    </template>
    <form
      v-else
      :class="b('form', formModifiers)"
      novalidate
      @submit.prevent="onSubmit"
    >
      <h2 :class="b('title')">
        {{ matchThemeTranslation(themeConfig.translations.formTitle) }}
      </h2>
      <div :class="b('message')">
        {{ matchThemeTranslation(themeConfig.translations.messageApology) }}<br />
        {{ matchThemeTranslation(themeConfig.translations.messageInstructions) }}
      </div>
      <div :class="b('form-row')">
        <component
          v-model="formData.salutation"
          :is="matchTheme(themeConfig.controls.cSelect)"
          control="c-select"
          :options="salutationOptions"
          :label="`${matchThemeTranslation(themeConfig.translations.inputLabelSalutation)}*`"
          :placeholder="matchThemeTranslation(themeConfig.translations.inputPlaceholderSalutation)"
          :state="v$.formData.salutation.$error ? 'error' : 'default'"
          name="salutation"
        >
          <template v-if="v$.formData.salutation.$error">
            {{ $t('c-electronic-instruction-problem.errorNotificationFieldRequired') }}
          </template>
        </component>
      </div>
      <div :class="b('form-row')">
        <component
          v-model="formData.firstName"
          :is="matchTheme(themeConfig.controls.eInput)"
          control="e-input"
          :label="`${matchThemeTranslation(themeConfig.translations.inputLabelFirstName)}*`"
          :placeholder="matchThemeTranslation(themeConfig.translations.inputPlaceholderFirstName)"
          :state="v$.formData.firstName.$error ? 'error' : 'default'"
          autocomplete="given-name"
          name="firstName"
        >
          <template v-if="v$.formData.firstName.$error">
            {{ $t('c-electronic-instruction-problem.errorNotificationFieldRequired') }}
          </template>
        </component>
      </div>
      <div :class="b('form-row')">
        <component
          v-model="formData.lastName"
          :is="matchTheme(themeConfig.controls.eInput)"
          control="e-input"
          :label="`${matchThemeTranslation(themeConfig.translations.inputLabelLastName)}*`"
          :placeholder="matchThemeTranslation(themeConfig.translations.inputPlaceholderLastName)"
          :state="v$.formData.lastName.$error ? 'error' : 'default'"
          autocomplete="family-name"
          name="lastName"
        >
          <template v-if="v$.formData.lastName.$error">
            {{ $t('c-electronic-instruction-problem.errorNotificationFieldRequired') }}
          </template>
        </component>
      </div>
      <div :class="b('form-row')">
        <component
          v-model="formData.companyName"
          :is="matchTheme(themeConfig.controls.eInput)"
          control="e-input"
          :label="`${matchThemeTranslation(themeConfig.translations.inputLabelCompanyName)}*`"
          :placeholder="matchThemeTranslation(themeConfig.translations.inputPlaceholderCompanyName)"
          :state="v$.formData.companyName.$error ? 'error' : 'default'"
          autocomplete="organization"
          name="companyName"
        >
          <template v-if="v$.formData.companyName.$error">
            {{ $t('c-electronic-instruction-problem.errorNotificationFieldRequired') }}
          </template>
        </component>
      </div>
      <div :class="b('form-row')">
        <component
          v-model="formData.email"
          :is="matchTheme(themeConfig.controls.eInput)"
          control="e-input"
          :label="`${matchThemeTranslation(themeConfig.translations.inputLabelEmail)}*`"
          :placeholder="matchThemeTranslation(themeConfig.translations.inputPlaceholderEmail)"
          :state="v$.formData.email.$error ? 'error' : 'default'"
          type="email"
          autocomplete="email"
          name="email"
        >
          <template v-if="v$.formData.email.$error">
            {{ $t('c-electronic-instruction-problem.errorNotificationEmail') }}
          </template>
        </component>
      </div>
      <div :class="b('form-row')">
        <component
          v-model="formData.phone"
          :is="matchTheme(themeConfig.controls.eInput)"
          control="e-input"
          :label="matchThemeTranslation(themeConfig.translations.inputLabelPhone)"
          :placeholder="matchThemeTranslation(themeConfig.translations.inputPlaceholderPhone)"
          inputmode="tel"
          name="phone"
        />
      </div>
      <div :class="b('form-row')">
        <component
          v-model="formData.comment"
          :is="matchTheme(themeConfig.controls.eTextarea)"
          control="e-textarea"
          :label="matchThemeTranslation(themeConfig.translations.inputLabelComment)"
          :placeholder="matchThemeTranslation(themeConfig.translations.inputPlaceholderComment)"
          name="comment"
        />
      </div>
      <div :class="b('form-row')">
        <e-checkbox
          v-model="formData.acceptGeneralTermsOfUse"
          :state="v$.formData.acceptGeneralTermsOfUse.$error ? 'error' : 'default'"
          name="acceptGeneralTermsOfUse"
        >
          <i18n-t
            keypath="c-electronic-instruction-problem.inputLabelGeneralTermsOfUse"
            scope="global"
          >
            <template #link>
              <a
                :href="sessionStore.urls.generalTermsOfUsePage"
                target="_blank"
                rel="noopener noreferrer"
                :class="b('terms-link')"
              >
                {{ $t('c-electronic-instruction-problem.inputLabelGeneralTermsOfUseLink') }}*
              </a>
            </template>
          </i18n-t>
        </e-checkbox>
      </div>
      <div :class="b('footer')">
        <e-button
          :class="b('button-submit')"
          :width="$viewport.isSm ? 'auto' : 'full'"
          variant="primary"
          type="submit"
          spacing="600"
        >
          {{ $t('c-electronic-instruction-problem.buttonLabelSubmit') }}
        </e-button>
        <button
          v-if="isTheme('default')"
          :class="b('button-back')"
          type="button"
          @click="close"
        >
          <e-icon-sprite
            icon="i-chevron-1--left"
            size="22"
          />
          {{ $t('c-electronic-instruction-problem.buttonLabelBack') }}
        </button>
      </div>
    </form>
  </c-modal>
</template>

<script>
  import { useVuelidate } from '@vuelidate/core';
  import { email, required } from '@vuelidate/validators';
  import useEifuStore from '@/stores/eifu';
  import useSessionStore from '@/stores/session';
  import cModal from '@/components/c-modal.vue';
  import eButton from '@/elements/e-button.vue';
  import eCheckbox from '@/elements/e-checkbox.vue';
  import eIconSprite from '@/elements/e-icon-sprite.vue';
  import eProgress from '@/elements/e-progress.vue';
  import themes from '@/mixins/themes.js';

  const initialFormData = {
    salutation: null,
    firstName: '',
    lastName: '',
    companyName: '',
    email: '',
    phone: '',
    comment: '',
    acceptGeneralTermsOfUse: false,
  };

  /**
   * Renders a modal with a form to report a problem with electronic instructions.
   */
  export default {
    name: 'c-electronic-instruction-problem',

    components: {
      eIconSprite,
      eCheckbox,
      eButton,
      eProgress,
      cModal,
    },
    mixins: [themes],

    props: {
      /**
       * Defines if the modal should be visible or not. '.sync' modifier is supported.
       */
      open: {
        type: Boolean,
        default: false,
      },

      /**
       * Accepts a query value, that will be sent with the report.
       */
      query: {
        type: String,
        default: null,
      },
    },

    emits: ['update:open'],

    setup() {
      return {
        v$: useVuelidate(),
        eifuStore: useEifuStore(),
        sessionStore: useSessionStore(),
      };
    },

    data() {
      return {
        /**
         * @type {Object} Holds the form data.
         */
        formData: {
          ...initialFormData,
        },

        /**
         * @type {Boolean} Flag for showing the confirmation a request was submitted.
         */
        showConfirmation: false,

        /**
         * @type {Array.<Object>} Holds the options for the salutation select.
         */
        salutationOptions: [
          {
            label: this.$t('c-electronic-instruction-problem.salutationOptionMs'),
            value: this.$t('c-electronic-instruction-problem.salutationOptionMs'),
          },
          {
            label: this.$t('c-electronic-instruction-problem.salutationOptionMr'),
            value: this.$t('c-electronic-instruction-problem.salutationOptionMr'),
          },
        ],

        /**
         * @type {Object} Holds theme specific options for the component.
         */
        themeConfig: {
          controls: {
            eInput: {
              default: 'e-input',
              sagemax: 'c-labeled-control',
            },
            eTextarea: {
              default: 'e-textarea',
              sagemax: 'c-labeled-control',
            },
            cSelect: {
              default: 'c-select',
              sagemax: 'c-labeled-control',
            },
          },
          translations: {
            titleConfirmation: {
              default: 'c-electronic-instruction-problem.titleConfirmation',
              sagemax: 'c-sagemax-electronic-instruction-problem.titleConfirmation',
            },
            textConfirmationMessage: {
              default: 'c-electronic-instruction-problem.textConfirmationMessage',
              sagemax: 'c-sagemax-electronic-instruction-problem.textConfirmationMessage',
            },
            formTitle: {
              default: 'c-electronic-instruction-problem.formTitle',
              sagemax: 'c-sagemax-electronic-instruction-problem.formTitle',
            },
            messageApology: {
              default: 'c-electronic-instruction-problem.messageApology',
              sagemax: 'c-sagemax-electronic-instruction-problem.messageApology',
            },
            messageInstructions: {
              default: 'c-electronic-instruction-problem.messageInstructions',
              sagemax: 'c-sagemax-electronic-instruction-problem.messageInstructions',
            },

            inputLabelSalutation: {
              default: 'c-electronic-instruction-problem.inputLabelSalutation',
              sagemax: 'c-sagemax-electronic-instruction-problem.inputLabelSalutation',
            },
            inputLabelFirstName: {
              default: 'c-electronic-instruction-problem.inputLabelFirstName',
              sagemax: 'c-sagemax-electronic-instruction-problem.inputLabelFirstName',
            },
            inputLabelLastName: {
              default: 'c-electronic-instruction-problem.inputLabelLastName',
              sagemax: 'c-sagemax-electronic-instruction-problem.inputLabelLastName',
            },
            inputLabelCompanyName: {
              default: 'c-electronic-instruction-problem.inputLabelCompanyName',
              sagemax: 'c-sagemax-electronic-instruction-problem.inputLabelCompanyName',
            },
            inputLabelEmail: {
              default: 'c-electronic-instruction-problem.inputLabelEmail',
              sagemax: 'c-sagemax-electronic-instruction-problem.inputLabelEmail',
            },
            inputLabelPhone: {
              default: 'c-electronic-instruction-problem.inputLabelPhone',
              sagemax: 'c-sagemax-electronic-instruction-problem.inputLabelPhone',
            },
            inputLabelComment: {
              default: 'c-electronic-instruction-problem.inputLabelComment',
              sagemax: 'c-sagemax-electronic-instruction-problem.inputLabelComment',
            },

            inputPlaceholderSalutation: {
              default: '',
              sagemax: 'c-sagemax-electronic-instruction-problem.inputPlaceholderSalutation',
            },
            inputPlaceholderFirstName: {
              default: '',
              sagemax: 'c-sagemax-electronic-instruction-problem.inputPlaceholderFirstName',
            },
            inputPlaceholderLastName: {
              default: '',
              sagemax: 'c-sagemax-electronic-instruction-problem.inputPlaceholderLastName',
            },
            inputPlaceholderCompanyName: {
              default: '',
              sagemax: 'c-sagemax-electronic-instruction-problem.inputPlaceholderCompanyName',
            },
            inputPlaceholderEmail: {
              default: '',
              sagemax: 'c-sagemax-electronic-instruction-problem.inputPlaceholderEmail',
            },
            inputPlaceholderPhone: {
              default: '',
              sagemax: 'c-sagemax-electronic-instruction-problem.inputPlaceholderPhone',
            },
            inputPlaceholderComment: {
              default: '',
              sagemax: 'c-sagemax-electronic-instruction-problem.inputPlaceholderComment',
            },
          },
        },
      };
    },

    /**
     * Vuelidate configuration.
     */
    validations: {
      formData: {
        salutation: {
          required,
        },
        firstName: {
          required,
        },
        lastName: {
          required,
        },
        companyName: {
          required,
        },
        email: {
          required,
          email,
        },
        acceptGeneralTermsOfUse: {
          checked: (value) => !!value,
        },
      },
    },

    computed: {
      /**
       * Gets/sets the open prop of the component.
       */
      computedOpen: {
        get() {
          return this.open;
        },
        set(open) {
          this.$emit('update:open', open);
        },
      },

      /**
       * Returns a definition of BEM modifiers.
       *
       * @returns {Object}
       */
      formModifiers() {
        const { theme } = this;

        return {
          theme,
        };
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      /**
       * Form submit handler.
       */
      onSubmit() {
        this.v$.$touch();

        if (this.v$.$pending || this.v$.$error) {
          return;
        }

        const { formData, $i18n, query } = this;

        this.eifuStore
          .apiReportProblem({
            locale: $i18n.locale,
            formData,
            query,
            referer: window.location.href, // TODO: store successful search queries on url (on c-electronic-instruction-banner).
          })
          .then(() => {
            this.showConfirmation = true;
            this.v$.$reset();
          });
      },

      /**
       * Closes the modal.
       */
      close() {
        this.computedOpen = false;
        this.reset();
      },

      /**
       * Resets the form values and modal state.
       */
      reset() {
        this.formData = {
          ...initialFormData,
        };
        this.v$.$reset();
        this.showConfirmation = false;
      },
    },
    // render() {},
  };
</script>

<style lang="scss">
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/extends' as *;
  @use '@/setup/scss/functions';

  .c-electronic-instruction-problem {
    $this: &;

    &--theme-sagemax {
      #{$this}__title {
        margin-bottom: functions.calc-rem(variables.$spacing--40);
      }

      #{$this}__message {
        margin-bottom: functions.rem(variables.$spacing--25);

        @include mixins.media(md) {
          margin-bottom: functions.rem(variables.$spacing--40);
        }
      }

      #{$this}__footer {
        justify-content: flex-end;
        margin-top: 0;
      }
    }

    &__title {
      @extend %hyphens;

      margin-bottom: functions.rem(variables.$spacing--25);

      @include mixins.media(md) {
        margin-bottom: functions.rem(variables.$spacing--30);
      }
    }

    &__message {
      margin-bottom: functions.rem(variables.$spacing--40);
      font-weight: variables.$font-weight--semi-bold;
    }

    &__form {
      &--theme-sagemax {
        #{$this}__terms-link {
          font-weight: variables.$font-weight--semi-bold;
          text-decoration: none;
        }
      }
    }

    &__form-row {
      margin-bottom: functions.rem(variables.$spacing--20);

      @include mixins.media(md) {
        width: 50%;
      }
    }

    &__footer {
      display: flex;
      flex-wrap: wrap;
      margin-top: functions.rem(variables.$spacing--40);

      @include mixins.media(sm) {
        flex-wrap: nowrap;
        justify-content: space-between;
      }

      @include mixins.media(md) {
        margin-top: functions.rem(variables.$spacing--60);
      }
    }

    &__button-submit {
      margin-bottom: functions.rem(variables.$spacing--30);

      @include mixins.media(sm) {
        order: 2;
        margin-bottom: 0;
      }
    }

    &__button-back {
      @extend %button-reset;
      @extend %link--primary-2;

      display: flex;
      align-items: center;

      .e-icon-sprite {
        width: functions.rem(22px);
        height: functions.rem(22px);
        margin-right: functions.rem(variables.$spacing--10);
      }
    }

    &__progress {
      display: flex;
      justify-content: center;

      .e-progress {
        width: functions.rem(30px);
        height: functions.rem(30px);
      }
    }

    &__close-button-wrapper {
      display: flex;
      justify-content: flex-end;
    }

    &__close-button {
      margin-top: functions.rem(variables.$spacing--40);
    }
  }
</style>
